import { useState, useEffect, useRef, useCallback } from 'react';
import { GetPatients } from '../services/rest/api';
import { TABLE } from '../constants/common';

export interface FilterObjectTypes {
  page: number;
  limit: number;
  total: number;
  filter_by: any;
}

export const usePatient = (
  searchTerm: string,
  patientListPagination: FilterObjectTypes
) => {
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('call');
  const [totalActive, setTotalActive] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchPatients = useCallback(async () => {
    if (activeView === 'favorite') return;

    const requestId = ++requestIdRef.current;
    const isActive = activeView === 'call' ? 'Y' : 'N';

    setLoading(true);
    setPatients([]);

    try {
      const response = await GetPatients({
        is_active: isActive,
        page: patientListPagination?.page?.toString() ?? '1',
        per_page: patientListPagination?.limit?.toString() ?? String(TABLE.DEFAULT_PAGINATION),
        search: debouncedSearch,
        filter_by: patientListPagination?.filter_by,
      });

      if (requestId !== requestIdRef.current) return;

      if (response?.code === '1') {
        setPatients(response.data ?? []);
        const total = response.total_data ?? 0;
        setTotalPatients(total);
        if (activeView === 'call') {
          setTotalActive(total);
        }
      } else {
        setPatients([]);
        setTotalPatients(0);
      }
    } catch (error) {
      console.error(error);
      if (requestId === requestIdRef.current) {
        setPatients([]);
        setTotalPatients(0);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    activeView,
    debouncedSearch,
    patientListPagination?.page,
    patientListPagination?.limit,
    patientListPagination?.filter_by,
  ]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const setActiveViewAndReset = (view: string) => {
    requestIdRef.current += 1;
    setPatients([]);
    setLoading(view !== 'favorite');
    setActiveView(view);
  };

  return {
    patients,
    loading,
    totalActive,
    activeView,
    setActiveView: setActiveViewAndReset,
    totalPatients,
  };
};
